import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  anchorLiveClock,
  applyLiveReplicaBatch,
  applyLiveSnapshot,
  applyPreflightDiagnostics,
  liveStreamStarted,
  liveStreamTerminal,
  receiveLiveReport,
  receiveSimulationDiagnostic,
  resetLiveStream,
  setLiveConnectionStatus,
} from '../features/simulation/simulationSlice';
import { AmhsLiveReplica } from '../features/simulation/amhsLiveReplica';
import type {
  AmhsStreamActiveResponse,
  AmhsStreamFirstFrame,
  AmhsStreamFrame,
  AmhsStreamRunRequest,
  AmhsStreamStopResponse,
} from '../types/amhsStream';
import {
  discoverAmhsStream,
  isAmhsStreamMissingError,
  isAmhsStreamMissingMessage,
  isAmhsStreamTimeoutError,
  openAmhsStreamSocket,
  stopAmhsStream,
  streamFrameMessage,
  validateAmhsRunRequest,
  type AmhsStreamSocket,
} from '../utils/amhsStreamApi';
import { toPreflightDiagnostics } from '../features/simulation/preflightDiagnostics';
import { normalizeLiveReport } from '../features/simulation/liveDispatchReport';
import {
  downloadAmhsStreamMessageDump,
  type AmhsStreamDumpRecord,
} from '../utils/amhsStreamMessageDump';
import { toAmhsStreamWireRequest } from '../features/simulation/amhsStreamWaitTimes';
import { transferWallHoldStore } from '../store/transferWallHoldStore';
import { liveOhtRuntimeStore } from '../store/liveOhtRuntimeStore';
import { useAppDispatch } from './redux';

const FLUSH_INTERVAL_MS = 100;
const RECONNECT_DELAY_MS = 1000;
/** A start that has not produced `stream_started` by then is reported as failed. */
const CONNECT_TIMEOUT_MS = 15_000;
/** A stop call that has not answered by then is reported so it can be retried. */
const STOP_TIMEOUT_MS = 10_000;
/**
 * Stop is scoped by `fabId` only, so it never fails with "Stream not found".
 * The API keeps live jobs in memory per process, and a deployment with
 * several tasks or workers answers each call from a random process. A
 * "no active stream" answer therefore means "not on the process that
 * replied", not "already stopped", and a fab can own a job on more than one
 * process when Start landed on different ones. So Stop sends a wave of
 * concurrent calls (one round trip), marks the UI stopped, and keeps sending
 * waves in the background while they still find something.
 */
const STOP_WAVE_SIZE = 6;
const STOP_MAX_WAVES = 4;
/**
 * Discovery has the same blind spot: the process that answers may not be the
 * one that owns this fab's job. Ask a few times before concluding that no
 * API-owned simulation exists, so Start attaches instead of creating a copy.
 */
const DISCOVER_ATTEMPTS = 3;
/** Stream dumps are diagnostic-only; recording every production frame causes unbounded churn. */
const RECORD_STREAM_MESSAGES =
  import.meta.env.DEV && import.meta.env.VITE_RECORD_AMHS_STREAM === 'true';
const MAX_RECORDED_STREAM_MESSAGES = 20_000;

function fingerprintStorageKey(streamId: string): string {
  return `amhs-stream-layout:${streamId}`;
}

function readStartedFingerprint(streamId: string): string | null {
  try {
    return localStorage.getItem(fingerprintStorageKey(streamId));
  } catch {
    return null;
  }
}

function writeStartedFingerprint(streamId: string, fingerprint: string): void {
  try {
    localStorage.setItem(fingerprintStorageKey(streamId), fingerprint);
  } catch {
    // Fingerprinting is advisory; stream playback still works without storage.
  }
}

function removeStartedFingerprint(streamId: string): void {
  try {
    localStorage.removeItem(fingerprintStorageKey(streamId));
  } catch {
    // Best-effort cleanup.
  }
}

export type UseAmhsLiveStreamOptions = {
  /** Stream occupancy key sent to discover / start / stop — the user id. */
  fabId: string;
  /** Layout identity for the first frame; `trackNetwork.fabId` is unchanged. */
  floorPlanId?: string | null;
  runRequest: AmhsStreamRunRequest | null;
  requestFingerprint: string | null;
};

export function useAmhsLiveStream({
  fabId,
  floorPlanId,
  runRequest,
  requestFingerprint,
}: UseAmhsLiveStreamOptions) {
  const dispatch = useAppDispatch();
  const [discovery, setDiscovery] = useState<AmhsStreamActiveResponse | null>(null);
  const [discovering, setDiscovering] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [observedFingerprint, setObservedFingerprint] = useState<string | null>(null);
  const [layoutMatchKnown, setLayoutMatchKnown] = useState(false);
  const [sessionActive, setSessionActive] = useState(false);
  const [starting, setStarting] = useState(false);
  const [stopPending, setStopPending] = useState(false);
  const startingRef = useRef(false);
  const socketRef = useRef<AmhsStreamSocket | null>(null);
  const replicaRef = useRef(new AmhsLiveReplica());
  const flushTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const connectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const discoverRef = useRef<() => Promise<unknown>>(async () => null);
  const activeStreamIdRef = useRef<string | null>(null);
  const stoppedOrUnmountedRef = useRef(false);
  const requestFingerprintRef = useRef(requestFingerprint);
  const connectRef = useRef<(streamId: string, reconnecting?: boolean) => void>(() => undefined);
  const recordedFramesRef = useRef<AmhsStreamDumpRecord[]>([]);
  const recordingSeqRef = useRef(0);
  const recordingStartedAtRef = useRef<string | null>(null);
  const dumpEmittedRef = useRef(true);

  const beginRecording = useCallback(() => {
    recordedFramesRef.current = [];
    recordingSeqRef.current = 0;
    recordingStartedAtRef.current = RECORD_STREAM_MESSAGES ? new Date().toISOString() : null;
    dumpEmittedRef.current = !RECORD_STREAM_MESSAGES;
  }, []);

  const dumpRecordedFrames = useCallback(() => {
    if (dumpEmittedRef.current) return;
    dumpEmittedRef.current = true;
    const records = recordedFramesRef.current;
    recordedFramesRef.current = [];
    downloadAmhsStreamMessageDump(records, {
      fabId,
      streamId: activeStreamIdRef.current,
      startedAt: recordingStartedAtRef.current,
      stoppedAt: new Date().toISOString(),
    });
  }, [fabId]);

  useEffect(() => {
    requestFingerprintRef.current = requestFingerprint;
  }, [requestFingerprint]);

  const flush = useCallback(() => {
    if (flushTimerRef.current) {
      clearTimeout(flushTimerRef.current);
      flushTimerRef.current = null;
    }
    replicaRef.current.setExecutionTimes(
      Object.fromEntries(
        Object.entries(liveOhtRuntimeStore.getFrame().byOhtId).map(([ohtId, runtime]) => [
          ohtId,
          runtime.localSimTimeS,
        ])
      )
    );
    dispatch(
      applyLiveReplicaBatch({
        batch: replicaRef.current.drain(),
        receivedAtMs: performance.now(),
      })
    );
  }, [dispatch]);

  const scheduleFlush = useCallback(() => {
    if (flushTimerRef.current) return;
    flushTimerRef.current = setTimeout(flush, FLUSH_INTERVAL_MS);
  }, [flush]);

  const clearConnectTimer = useCallback(() => {
    if (connectTimerRef.current) {
      clearTimeout(connectTimerRef.current);
      connectTimerRef.current = null;
    }
  }, []);

  const handleFrame = useCallback(
    (frame: AmhsStreamFrame) => {
      const receivedAtMs = performance.now();
      // Any frame proves the server picked the socket up.
      clearConnectTimer();
      if (
        !dumpEmittedRef.current &&
        recordedFramesRef.current.length < MAX_RECORDED_STREAM_MESSAGES
      ) {
        recordingSeqRef.current += 1;
        recordedFramesRef.current.push({
          seq: recordingSeqRef.current,
          receivedAt: new Date().toISOString(),
          type: frame.type,
          frame,
        });
      }
      if (frame.type === 'stream_started') {
        transferWallHoldStore.reset();
        liveOhtRuntimeStore.reset();
        activeStreamIdRef.current = frame.streamId;
        replicaRef.current.setMaxRetainedLegs(frame.maxRetainedLegs);
        replicaRef.current.setSimSpeed(frame.simSpeed);
        dispatch(
          liveStreamStarted({
            fabId: frame.fabId,
            streamId: frame.streamId,
            simTimeS: frame.simTimeS,
            simSpeed: frame.simSpeed,
            reconnected: frame.reconnected,
            receivedAtMs,
          })
        );
        startingRef.current = false;
        setStarting(false);
        setSessionActive(true);
        setDiscovery({
          active: true,
          streamId: frame.streamId,
          fabId: frame.fabId,
          status: 'running',
          simTimeS: frame.simTimeS,
          simSpeed: frame.simSpeed,
        });
        if (!frame.reconnected && requestFingerprint) {
          writeStartedFingerprint(frame.streamId, requestFingerprint);
          setObservedFingerprint(requestFingerprint);
          setLayoutMatchKnown(true);
        }
        return;
      }
      if (frame.type === 'stream_snapshot') {
        replicaRef.current.setSimSpeed(frame.simSpeed);
        replicaRef.current.applySnapshot(frame);
        dispatch(applyLiveSnapshot({ frame, receivedAtMs }));
        flush();
        return;
      }
      if (frame.type === 'heartbeat') {
        dispatch(
          anchorLiveClock({
            simTimeS: frame.simTimeS,
            receivedAtMs,
            jobStatus: frame.status,
          })
        );
        return;
      }
      if (frame.type === 'simulation_diagnostic') {
        dispatch(receiveSimulationDiagnostic(frame));
        return;
      }
      if (
        frame.type === 'stream_stopped' ||
        frame.type === 'stream_closed' ||
        frame.type === 'error'
      ) {
        const message = streamFrameMessage(frame);
        const streamGone = frame.type === 'error' && isAmhsStreamMissingMessage(message);
        const terminalLiveReport =
          frame.type === 'stream_stopped' ? normalizeLiveReport(frame.payload?.liveReport) : null;
        if (terminalLiveReport) {
          dispatch(receiveLiveReport(terminalLiveReport));
        }
        stoppedOrUnmountedRef.current = true;
        dumpRecordedFrames();
        if (streamGone || (frame.type === 'stream_stopped' && activeStreamIdRef.current)) {
          if (activeStreamIdRef.current) {
            removeStartedFingerprint(activeStreamIdRef.current);
          }
          activeStreamIdRef.current = null;
          startingRef.current = false;
          setStarting(false);
          setSessionActive(false);
          setDiscovery({ active: false, streamId: null, fabId });
          setObservedFingerprint(requestFingerprint);
          setLayoutMatchKnown(false);
          setActionError(null);
          dispatch(resetLiveStream());
          // The job we were attached to is gone (typically an API restart).
          // Ask the API what, if anything, is running now so the buttons show
          // Start or Resume without a page reload.
          setTimeout(() => void discoverRef.current(), 0);
        } else if (frame.type === 'error') {
          startingRef.current = false;
          setStarting(false);
          const errMsg = message ?? 'AMHS stream failed.';
          setActionError(errMsg);
          dispatch(
            liveStreamTerminal({
              status: frame.status,
              error: errMsg,
            })
          );
        } else {
          dispatch(
            liveStreamTerminal({
              status: frame.status,
              error: null,
            })
          );
        }
        socketRef.current?.close();
        socketRef.current = null;
        transferWallHoldStore.reset();
        liveOhtRuntimeStore.reset();
        return;
      }
      replicaRef.current.enqueue(frame);
      // Column updates (crane raw→processed) must land before the 100ms batch
      // coalesce — processed dots otherwise lag until the next inventory rollup.
      if (frame.type === 'stocker_inventory') {
        flush();
        return;
      }
      scheduleFlush();
    },
    [
      clearConnectTimer,
      dispatch,
      dumpRecordedFrames,
      fabId,
      flush,
      requestFingerprint,
      scheduleFlush,
    ]
  );

  const connect = useCallback(
    (
      streamId: string | null,
      reconnecting = false,
      createRequest?: AmhsStreamRunRequest,
      options?: { restart?: boolean }
    ) => {
      socketRef.current?.close();
      clearConnectTimer();
      stoppedOrUnmountedRef.current = false;
      if (!reconnecting) beginRecording();
      setActionError(null);
      dispatch(
        setLiveConnectionStatus({
          status: reconnecting ? 'reconnecting' : 'connecting',
          fabId,
          error: null,
        })
      );

      const layoutId = floorPlanId?.trim();
      const firstFrame: AmhsStreamFirstFrame = {
        mode: 'live',
        fabId,
        ...(layoutId ? { floorPlanId: layoutId } : {}),
        ...(streamId ? { streamId } : {}),
        ...(options?.restart ? { restart: true } : {}),
        runRequest: streamId ? {} : createRequest ? toAmhsStreamWireRequest(createRequest) : {},
      };

      /**
       * The socket ended before the server's first frame, so there is no job
       * to reconnect to. Report it; leaving `starting` set kept the Start
       * button on "Starting…" with nothing to click.
       */
      const failBeforeStart = (message: string) => {
        if (stoppedOrUnmountedRef.current || activeStreamIdRef.current) return;
        startingRef.current = false;
        setStarting(false);
        setSessionActive(false);
        setActionError(message);
        dispatch(setLiveConnectionStatus({ status: 'failed', fabId, error: message }));
      };

      socketRef.current = openAmhsStreamSocket(firstFrame, {
        onFrame: handleFrame,
        onError: (error) => {
          clearConnectTimer();
          startingRef.current = false;
          setStarting(false);
          setActionError(error.message);
          dispatch(setLiveConnectionStatus({ status: 'failed', fabId, error: error.message }));
        },
        onClose: () => {
          clearConnectTimer();
          socketRef.current = null;
          if (stoppedOrUnmountedRef.current) return;
          if (!activeStreamIdRef.current) {
            failBeforeStart('The live stream closed before it started. Try again.');
            return;
          }
          dispatch(setLiveConnectionStatus({ status: 'reconnecting', fabId }));
          reconnectTimerRef.current = setTimeout(() => {
            const id = activeStreamIdRef.current;
            if (id && !stoppedOrUnmountedRef.current) connectRef.current(id, true);
          }, RECONNECT_DELAY_MS);
        },
      });

      connectTimerRef.current = setTimeout(() => {
        connectTimerRef.current = null;
        if (stoppedOrUnmountedRef.current || activeStreamIdRef.current) return;
        socketRef.current?.close();
        socketRef.current = null;
        failBeforeStart(
          `The API did not start the live stream within ${CONNECT_TIMEOUT_MS / 1000} seconds. Try again.`
        );
      }, CONNECT_TIMEOUT_MS);
    },
    [beginRecording, clearConnectTimer, dispatch, fabId, floorPlanId, handleFrame]
  );
  useEffect(() => {
    connectRef.current = (streamId, reconnecting = false) => connect(streamId, reconnecting);
  }, [connect]);

  const discover = useCallback(async () => {
    setDiscovering(true);
    setActionError(null);
    dispatch(setLiveConnectionStatus({ status: 'discovering', fabId, error: null }));
    try {
      // The answering process may not own this fab's job; ask a few times
      // and keep the first positive answer.
      let result = await discoverAmhsStream(fabId);
      for (let attempt = 2; attempt <= DISCOVER_ATTEMPTS && !result.active; attempt += 1) {
        result = await discoverAmhsStream(fabId);
      }
      setDiscovery(result);
      activeStreamIdRef.current = result.active ? result.streamId : null;
      const stored =
        result.active && result.streamId ? readStartedFingerprint(result.streamId) : null;
      setObservedFingerprint(stored ?? requestFingerprintRef.current);
      setLayoutMatchKnown(Boolean(stored));
      dispatch(setLiveConnectionStatus({ status: 'idle', fabId }));
      return result;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Could not discover AMHS stream.';
      setActionError(message);
      dispatch(setLiveConnectionStatus({ status: 'failed', fabId, error: message }));
      return null;
    } finally {
      setDiscovering(false);
    }
  }, [dispatch, fabId]);

  useEffect(() => {
    discoverRef.current = discover;
  }, [discover]);

  useEffect(() => {
    stoppedOrUnmountedRef.current = false;
    const discoveryTimer = setTimeout(() => void discover(), 0);
    return () => {
      clearTimeout(discoveryTimer);
      stoppedOrUnmountedRef.current = true;
      socketRef.current?.close();
      socketRef.current = null;
      if (flushTimerRef.current) clearTimeout(flushTimerRef.current);
      if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
      if (connectTimerRef.current) clearTimeout(connectTimerRef.current);
      if (activeStreamIdRef.current) {
        activeStreamIdRef.current = null;
        dispatch(resetLiveStream());
        transferWallHoldStore.reset();
      }
    };
  }, [discover, dispatch]);

  const preflight = useCallback(
    async (request: AmhsStreamRunRequest) => {
      try {
        const validation = await validateAmhsRunRequest(request);
        dispatch(applyPreflightDiagnostics(toPreflightDiagnostics(validation, { fabId })));
      } catch {
        dispatch(applyPreflightDiagnostics([]));
      }
    },
    [dispatch, fabId]
  );

  const start = useCallback(() => {
    if (!runRequest) {
      setActionError('The current layout is not ready for live simulation.');
      return;
    }
    if (startingRef.current) return;
    startingRef.current = true;
    setStarting(true);
    setSessionActive(true);
    setActionError(null);
    dispatch(setLiveConnectionStatus({ status: 'connecting', fabId, error: null }));

    try {
      if (discovery?.active && discovery.streamId) {
        replicaRef.current = new AmhsLiveReplica();
        connect(discovery.streamId);
        return;
      }
      const wireRequest = toAmhsStreamWireRequest(runRequest);
      void preflight(wireRequest);
      replicaRef.current = new AmhsLiveReplica(runRequest.liveStream.maxRetainedLegs);
      connect(null, false, runRequest);
    } catch (error) {
      startingRef.current = false;
      setStarting(false);
      setSessionActive(false);
      const message = error instanceof Error ? error.message : 'Could not start AMHS stream.';
      setActionError(message);
      dispatch(setLiveConnectionStatus({ status: 'failed', fabId, error: message }));
    }
  }, [connect, discovery, dispatch, fabId, preflight, runRequest]);

  const resume = useCallback(() => {
    if (!discovery?.active || !discovery.streamId) return;
    if (startingRef.current) return;
    startingRef.current = true;
    setStarting(true);
    setSessionActive(true);
    replicaRef.current = new AmhsLiveReplica();
    connect(discovery.streamId);
  }, [connect, discovery]);

  const stop = useCallback(async (): Promise<boolean> => {
    const streamId = activeStreamIdRef.current ?? discovery?.streamId;
    clearConnectTimer();
    if (!streamId) {
      stoppedOrUnmountedRef.current = true;
      socketRef.current?.close();
      socketRef.current = null;
      startingRef.current = false;
      setStarting(false);
      setSessionActive(false);
      setDiscovery({ active: false, streamId: null, fabId });
      dispatch(resetLiveStream());
      return true;
    }
    stoppedOrUnmountedRef.current = true;
    setStopPending(true);
    dispatch(setLiveConnectionStatus({ status: 'stopping', fabId }));
    const markStopped = () => {
      dumpRecordedFrames();
      removeStartedFingerprint(streamId);
      socketRef.current?.close();
      socketRef.current = null;
      activeStreamIdRef.current = null;
      startingRef.current = false;
      setStarting(false);
      setSessionActive(false);
      setDiscovery({ active: false, streamId: null, fabId });
      setObservedFingerprint(requestFingerprint);
      setLayoutMatchKnown(false);
      setActionError(null);
      dispatch(resetLiveStream());
    };
    try {
      // Stop by fab, not by stream id: the id is only known to the API process
      // that started the job, and the request may be answered by another one.
      // One call is enough on an API that broadcasts the stop to its other
      // processes (`broadcast: true`). An older API answers only for itself,
      // so then ask several processes at once: one wave of concurrent calls
      // costs one round trip, and each call lands on a random process. The UI
      // is marked stopped after the first answer; further waves run in the
      // background while they still find something. Stopping a stream that is
      // already gone is a success.
      const first = await stopAmhsStream({ fabId, reason: 'ui_stop' }, { timeoutMs: STOP_TIMEOUT_MS });
      if (first.broadcast) {
        markStopped();
        return true;
      }
      const sendWave = async (): Promise<boolean> => {
        const results = await Promise.allSettled(
          Array.from({ length: STOP_WAVE_SIZE }, () =>
            stopAmhsStream({ fabId, reason: 'ui_stop' }, { timeoutMs: STOP_TIMEOUT_MS })
          )
        );
        const fulfilled = results.filter(
          (r): r is PromiseFulfilledResult<AmhsStreamStopResponse> => r.status === 'fulfilled'
        );
        if (fulfilled.length === 0) {
          const first = results[0];
          throw first.status === 'rejected' ? first.reason : new Error('Could not stop AMHS stream.');
        }
        return fulfilled.some((r) => r.value.stopped);
      };
      const firstWaveStopped = (await sendWave()) || first.stopped;
      markStopped();
      if (firstWaveStopped) {
        void (async () => {
          for (let wave = 2; wave <= STOP_MAX_WAVES; wave += 1) {
            try {
              if (!(await sendWave())) return;
            } catch {
              return;
            }
          }
        })();
      }
      return true;
    } catch (error) {
      if (isAmhsStreamMissingError(error)) {
        markStopped();
        return true;
      }
      // Keep the stream id so the Stop button stays clickable for a retry.
      stoppedOrUnmountedRef.current = false;
      const message = isAmhsStreamTimeoutError(error)
        ? `Stop did not complete within ${STOP_TIMEOUT_MS / 1000} seconds. Try again.`
        : error instanceof Error
          ? error.message
          : 'Could not stop AMHS stream.';
      setActionError(message);
      dispatch(setLiveConnectionStatus({ status: 'failed', fabId, error: message }));
      return false;
    } finally {
      setStopPending(false);
    }
  }, [
    clearConnectTimer,
    discovery?.streamId,
    dispatch,
    dumpRecordedFrames,
    fabId,
    requestFingerprint,
  ]);

  /**
   * Replace the fab's server job with a new one built from the current layout,
   * in a single handshake. The first frame carries `restart: true`, so the API
   * stops the existing job even when the plan is unchanged, which a plain
   * Start would otherwise attach to.
   */
  const startFresh = useCallback(() => {
    if (!runRequest) {
      setActionError('The current layout is not ready for live simulation.');
      return;
    }
    if (startingRef.current) return;
    startingRef.current = true;
    setStarting(true);
    setSessionActive(true);
    setActionError(null);
    if (activeStreamIdRef.current) removeStartedFingerprint(activeStreamIdRef.current);
    activeStreamIdRef.current = null;
    dispatch(resetLiveStream());
    dispatch(setLiveConnectionStatus({ status: 'connecting', fabId, error: null }));
    const wireRequest = toAmhsStreamWireRequest(runRequest);
    void preflight(wireRequest);
    replicaRef.current = new AmhsLiveReplica(runRequest.liveStream.maxRetainedLegs);
    connect(null, false, runRequest, { restart: true });
  }, [connect, dispatch, fabId, preflight, runRequest]);

  const restart = useCallback(async () => {
    const stopped = await stop();
    if (!stopped) return;
    if (!runRequest) return;
    startingRef.current = true;
    setStarting(true);
    setSessionActive(true);
    dispatch(setLiveConnectionStatus({ status: 'connecting', fabId, error: null }));
    const wireRequest = toAmhsStreamWireRequest(runRequest);
    void preflight(wireRequest);
    replicaRef.current = new AmhsLiveReplica(runRequest.liveStream.maxRetainedLegs);
    connect(null, false, runRequest);
  }, [connect, dispatch, fabId, preflight, runRequest, stop]);

  const layoutChanged = useMemo(
    () =>
      Boolean(
        discovery?.active &&
        observedFingerprint &&
        requestFingerprint &&
        observedFingerprint !== requestFingerprint
      ),
    [discovery?.active, observedFingerprint, requestFingerprint]
  );

  return {
    discovery,
    discovering,
    starting,
    stopPending,
    sessionActive,
    actionError,
    layoutChanged,
    layoutMatchKnown,
    start,
    startFresh,
    resume,
    stop,
    restart,
    rediscover: discover,
  };
}
